import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause } from 'lucide-react';

import './mini-voice-preview.css';

const formatRemainingTime = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) {
        return '0:00';
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${String(secs).padStart(2, '0')}`;
};

/**
 * Compact play/pause + scrubber + remaining time. Only one row may play at a time
 * when `activePlayerId` / `setActivePlayerId` are provided (match `playerId`).
 */
const MiniVoicePreview = ({ src, playerId, activePlayerId, setActivePlayerId }) => {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    useEffect(() => {
        if (activePlayerId !== playerId && audioRef.current) {
            if (!audioRef.current.paused) {
                audioRef.current.pause();
            }
            setIsPlaying(false);
        }
    }, [activePlayerId, playerId]);

    const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
    const displayTime =
        duration > 0
            ? formatRemainingTime(Math.max(0, duration - currentTime))
            : formatRemainingTime(0);

    const togglePlay = () => {
        if (!audioRef.current) {
            return;
        }
        if (isPlaying) {
            audioRef.current.pause();
            setIsPlaying(false);
            setActivePlayerId(null);
        } else {
            setActivePlayerId(playerId);
            audioRef.current
                .play()
                .then(() => setIsPlaying(true))
                .catch(() => {
                    setIsPlaying(false);
                    setActivePlayerId(null);
                });
        }
    };

    const handleSeek = (e) => {
        const next = Number(e.target.value);
        if (!audioRef.current || Number.isNaN(next)) {
            return;
        }
        audioRef.current.currentTime = next;
        setCurrentTime(next);
    };

    return (
        <div className="mini-voice-preview">
            <audio
                ref={audioRef}
                src={src}
                preload="metadata"
                className="mini-voice-preview__audio"
                onLoadedMetadata={(ev) => setDuration(ev.target.duration || 0)}
                onTimeUpdate={(ev) => setCurrentTime(ev.target.currentTime || 0)}
                onEnded={() => {
                    setIsPlaying(false);
                    setCurrentTime(0);
                    setActivePlayerId(null);
                }}
            />
            <button
                type="button"
                className={`mini-voice-preview__play ${isPlaying ? 'mini-voice-preview__play--playing' : ''}`}
                onClick={togglePlay}
                aria-label={isPlaying ? 'Pause' : 'Play'}
            >
                {isPlaying ? (
                    <Pause size={11} />
                ) : (
                    <Play size={11} className="mini-voice-preview__play-icon" />
                )}
            </button>
            <input
                type="range"
                className="mini-voice-preview__progress"
                min={0}
                max={duration || 0}
                step={0.1}
                value={Math.min(currentTime, duration || 0)}
                onChange={handleSeek}
                style={{
                    background: `linear-gradient(to right, #2f6bff ${progressPercent}%, #dbe6ff ${progressPercent}%)`,
                }}
            />
            <span className="mini-voice-preview__time">{displayTime}</span>
        </div>
    );
};

export default MiniVoicePreview;
